<script>
import professionalMixin from "@/mixins/accountable/professionalMixin";
import cycleMixin from "@/mixins/cycleMixin";

export default {
  mixins: [professionalMixin, cycleMixin],

  props: {
    payable: {
      type: Object,
      required: true
    }
  },

  methods: {
    redirect() {
      if (this.getPendingBoleto(this.payable.payments)) {
        this.$router.push({
          name: "cycle",
          params: { id: this.payable.id }
        });
      } else {
        this.$router.push({
          name: "cycle-checkout",
          params: { cycle_id: this.payable.id }
        });
      }
    }
  },

  computed: {
    professional: function() {
      return this.payable.subscription.professional;
    },

    daysLeft: function() {
      const daysLeft = this.daysBeforeCycleExpiration(this.payable);

      if (daysLeft < 0) {
        return false;
      }

      if (daysLeft === 0) {
        return "HOJE";
      }

      return `${daysLeft} DIA${daysLeft > 1 ? "S" : ""}`;
    },

    currentState: function() {
      if (this.validPayment) {
        return "AGUARDANDO CONFIRMAÇÃO";
      }

      if (this.isCycleExpired(this.payable)) {
        return "CICLO EXPIRADO";
      }

      return "VENCIMENTO";
    },

    noValidPayment: function() {
      return this.daysLeft && !this.validPayment;
    },

    validPayment: function() {
      return this.getPendingBoleto(this.payable.payments);
    }
  }
};
</script>

<template>
  <div
  class="cycle-card"
    :class="{
      'cycle-card-schedule': noValidPayment,
      'cycle-card-waiting': validPayment
    }"
    @click="redirect"
  >
    <div class="header">
      <div class="left">
        <img :src="professional | profilePicture" />

        <div class="header-content">
          <span class="name">
            {{ professional | firstName }}
          </span>

          <span class="cycle-count">
            <b>{{ professional | orderCount | minOrderCount }} limpezas</b> <span> realizadas</span>
          </span>
        </div>
      </div>

      <div class="right">
        <span class="text">{{ currentState }}</span>

        <div class="countdown" v-if="daysLeft && !validPayment">
          <span>{{ daysLeft }}</span>
        </div>
      </div>
    </div>

    <div class="body">
      <span class="description">
        Detalhes da assinatura
      </span>

      <div class="body-content">
        <div class="date">
          <img src="/static/calendar-outlined.svg" />
          <span>
            Ciclo de <b>{{ payable | cycleMonth }}</b> ({{ payable | cycleOrderCount }} limpezas)
          </span>
        </div>

        <div class="duration">
          <img src="/static/clock-outlined.svg" />
          <span>{{ payable | cycleOrderDuration }}</span>
        </div>

        <div class="location">
          <img src="/static/pin.svg" />
          <span>{{ payable | cycleAddressName }}</span>
        </div>
      </div>
    </div>

    <div class="footer" v-if="!isCycleExpired(payable)">
      <span>{{ validPayment ? "ver ciclo" : "finalizar pagamento" }}</span>

      <img src="/static/basic/arrow-next.svg" />
    </div>
  </div>
</template>

<style scoped>
.cycle-card {
  width: calc(100% - 24px - 2px);
  padding: 12px;
  background-color: #fff;
  border: solid 1px #e2e2e2;
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  user-select: none;
}

.cycle-card::before {
  width: calc(100% + 2px);
  height: 6px;
  position: absolute;
  top: -1px;
  left: -1px;
  content: "";
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;
}

.cycle-card-schedule::before {
  background-color: #2196f3;
}

.cycle-card-waiting::before {
  background-color: #ffc107;
}

.cycle-card:not(:last-child) {
  margin-bottom: 12px;
}

.header {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.left {
  width: 70%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
}

.header > .left > img {
  width: 36px;
  height: 36px;
  margin-right: 8px;
  border-radius: 50%;
}

.header-content {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.header-content > .name {
  font-size: 16px;
  font-weight: 400;
}

.header-content > .cycle-count {
  font-size: 12px;
}

.header-content > .cycle-count > b {
  font-weight: 400;
  color: #603285;
}

.header-content > .cycle-count > span {
  display: none;
}

.right {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
}

.right > .text {
  font-size: 10px;
  font-weight: 500;
  text-align: end;
}

.right > .countdown {
  font-size: 16px;
  font-weight: 500;
}

.progress-bar {
  width: 100%;
  height: 4px;
  position: relative;
}

.progress-bar-total,
.progress-bar-elapsed {
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: #af006b;
  border-radius: 2px;
}

.progress-bar-total {
  opacity: 0.25;
}

/* body */
.body {
  width: 100%;
  margin-top: 16px;
  padding: 12px 0;
  position: relative;
  border: solid 1px #e2e2e2;
  border-left: none;
  border-right: none;
}

.description {
  position: absolute;
  top: -8px;
  padding-right: 8px;
  font-size: 12px;
  font-weight: 400;
  background-color: #fff;
}

.body-content {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
}

.body-content img {
  width: 14px;
  margin-right: 4px;
}

.date,
.duration,
.location {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 13px;
}

.date b {
  font-weight: 500;
}

/* footer */
.footer {
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.footer > span {
  font-size: 14px;
  font-weight: 400;
  color: #603285;
}

.footer > img {
  width: 14px;
  height: 14px;
}

@media (min-width: 360px) {
  .header-content > .cycle-count > span {
    display: inline;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Cycle/Components/Card.vue